import { PIECE_TYPES } from "@/game/pieces";
import type { Club } from "@/lib/api";
import { Tetromino } from "./Tetromino";

interface HeroProps {
  readonly club: Club | null;
}

/**
 * Each piece is claimed by a member. Written exactly as they spell it — these
 * are handles, so the casing is theirs to set, not the stylesheet's.
 */
const PIECE_NAMES: Record<string, string> = {
  I: "Inkl1ng",
  J: "John Unc (Bennxt)",
  L: "Lobster",
  O: "Otatopling2",
  S: "Sybaugohan",
  T: "Teesa",
  Z: "Zieradni",
};

export function Hero({ club }: HeroProps) {
  const discord = club?.links.find((link) => link.primary)?.url;

  return (
    <section id="top" className="lattice relative border-b-2 border-ink">
      <div className="shell grid items-center gap-10 py-14 md:py-20 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
        {/* ── Wordmark ─────────────────────────────────────────────── */}
        <div>
          <p className="meta mb-6 inline-block border-2 border-ink bg-paper px-2.5 py-1.5">
            {club?.campus ?? "University of California, Irvine"}
          </p>

          <h1 className="text-[clamp(3.25rem,13vw,8.5rem)]">
            <span className="block">Tetris</span>
            <span className="block">
              At{" "}
              <span
                className="inline-block bg-ink px-3 pb-1 text-field"
                // A slight tilt, like a sticker pressed on by hand.
                style={{ transform: "rotate(-1.5deg)" }}
              >
                UCI
              </span>
            </span>
          </h1>

          <p className="mt-7 max-w-[36ch] text-lg leading-snug font-medium sm:text-xl">
            #5 seeded CTL team
          </p>
          <p className="mt-3 max-w-[46ch] text-base leading-relaxed opacity-80">
            {club?.blurb ??
              "Whether you play on an NES, on TETR.IO, or on Jstris, this is the place for you."}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {discord ? (
              <a className="btn" href={discord} target="_blank" rel="noopener noreferrer">
                Join the Discord
              </a>
            ) : null}
            <a className="btn btn--ghost" href="#roster">
              See the roster
            </a>
          </div>
        </div>

        {/* ── The sticker ──────────────────────────────────────────── */}
        <div className="justify-self-center lg:justify-self-end">
          <img
            src="/logo.webp"
            alt="Tetris At UCI club logo"
            width={340}
            height={340}
            className="offset-ink w-[min(340px,72vw)] border-2 border-ink"
            style={{ transform: "rotate(2deg)" }}
          />
        </div>
      </div>

      {/* ── The bag, laid out flat ───────────────────────────────────
          Seven pieces, in the order the site itself uses them. */}
      <div className="border-t-2 border-ink bg-paper">
        {/* Stacked on narrow screens: once the names wrap onto several rows the
            label would otherwise sit alongside whichever piece landed last. */}
        <div
          className="shell flex flex-col gap-4 py-5 sm:flex-row sm:flex-wrap sm:items-end
            sm:justify-between sm:gap-x-6 sm:gap-y-5"
        >
          <span className="meta opacity-70">The bag</span>
          <ul className="flex flex-1 flex-wrap items-end justify-around gap-x-6 gap-y-4">
            {PIECE_TYPES.map((piece) => (
              <li key={piece} className="flex flex-col items-center gap-2">
                <Tetromino type={piece} cell={11} title={`${piece} piece`} />
                <span className="meta text-[0.5625rem] whitespace-nowrap opacity-60">
                  {piece} ·{" "}
                  {/* normal-case: `meta` uppercases, which would flatten a
                      handle like "zieradni" or the 1 in "Inkl1ng". */}
                  <span className="normal-case">{PIECE_NAMES[piece]}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
